import type { NexoyaOptimizedContentLabel } from '../../../../types';

import type { ContentLabelRowRaw, RowRaw } from './optimizationDetailsTableTypes';

/**
 * The Optimize "By Label" view's pure logic: the grouped rows, and the sentence
 * under the table.
 *
 * It sits beside `utils.tsx` rather than inside it so it can be a node test —
 * `utils.tsx` pulls React, `FormattedCurrency` and the whole column tree in
 * behind it — and so the merge below can be TYPED. Its sibling
 * `mergeRowsAndPerformanceData` (utils.tsx:402) escapes the type checker through
 * a `@ts-expect-error` that makes the whole expression `any`; this one does not,
 * which is why `initialBudgetDaily` is defaulted rather than left undefined.
 */

type MergeArgs = {
  /** The chosen category's buckets, straight off `OptimizationContentLabelPerformance`. */
  contentLabels: NexoyaOptimizedContentLabel[] | null | undefined;
  /** The per-content rows the table already built, read only for their shared shape. */
  rows: RowRaw[];
  /** The category's hex colour. Every value in a category shares it. */
  contentLabelCategoryColor?: string;
};

/**
 * One aggregate row per bucket, with the null bucket LAST.
 *
 * Always an array. `OptimizationProposalTable.tsx:215` calls `.some(...)` on the
 * grouped rows without a guard, so a merge that can answer `undefined` is a
 * white screen one empty backend array away — which is exactly the latent defect
 * the existing labels/channels merge carries.
 *
 * The null bucket is moved to the end here rather than left in server order
 * because it is not a peer of the labelled rows: it is the remainder, it is
 * excluded from the proposal, and the banner under the table explains it. Note
 * that SORTING the category column will still float it to the top — nullish
 * keys are their own tier in `sortTypes` — which is a known, accepted wart.
 */
export function mergeContentLabelRowsAndPerformanceData({
  contentLabels,
  rows,
  contentLabelCategoryColor,
}: MergeArgs): ContentLabelRowRaw[] {
  if (!contentLabels?.length) return [];

  const template = rows[0];

  const merged: ContentLabelRowRaw[] = contentLabels.map((bucket) => ({
    isExcluded: false,
    providerId: template?.providerId,
    // Defaults carried over from the sibling merge: a grouped row has no content
    // of its own, and these keep the shared cells from rendering something
    // misleading rather than nothing.
    contentId: template?.contentId,
    title: template?.title,
    titleLink: template?.titleLink,
    isPerforming: template?.isPerforming,
    status: template?.status,
    initialBudgetDaily: template?.initialBudgetDaily ?? 0,
    lifetimeBudgetSegments: null,
    initialBiddingStrategy: null,
    proposedBiddingStrategy: null,
    biddingStrategyChangePercent: null,
    impactGroup: null,
    target: bucket?.target,
    // `spent` is the previous daily budget, as everywhere else in this table.
    previousDailyBudget: bucket?.budget?.spent,
    proposedDailyBudget: bucket?.budget?.proposed,
    proposedDailyBudgetChange: bucket?.budget?.changePercent,
    contentLabelCategoryValue: bucket?.contentLabelCategoryValue ?? null,
    contentLabelCategoryColor,
    isUnlabeledBucket: bucket?.contentLabelCategoryValue == null,
    // Positional merge: the bucket's funnel steps carry metrics but no titles, so
    // the titles come from the first content row's steps, which are in the same
    // portfolio order.
    funnelSteps:
      template?.funnelSteps?.map((optimizedFunnelStep, idx) => ({
        funnelStepId: optimizedFunnelStep?.funnelStep?.funnelStepId,
        title: optimizedFunnelStep?.funnelStep?.title,
        type: optimizedFunnelStep?.funnelStep?.type,
        ...bucket?.funnelSteps?.[idx],
      })) ?? [],
  })) as ContentLabelRowRaw[];

  return [...merged.filter((row) => !row.isUnlabeledBucket), ...merged.filter((row) => row.isUnlabeledBucket)];
}

export type ContentLabelBucketWarning = {
  /** Everything before the money. */
  lead: string;
  /** Everything after the money, closing the first sentence. */
  trail: string;
  /** Rendered by the caller through `FormattedCurrency`, so this stays locale-free. */
  bucketSpend: number;
  /** The second sentence, split around the Settings link. */
  resolveLead: string;
  resolveTrail: string;
};

type WarningArgs = {
  needsDecisionCount: number;
  unlabeledCount: number;
  categoryName: string;
  /** The null bucket's average daily spend. `null`/`undefined` means there is no null bucket. */
  bucketSpend: number | null | undefined;
};

/**
 * fig3's banner copy, split around the two things a string cannot carry: the
 * money (which must render through `FormattedCurrency`, in the team's currency
 * and number format) and the Settings link.
 *
 * `null` means render nothing — no empty shell and no "all good" state. Two ways
 * to get there: nothing sits outside a label (both counts 0), or the payload has
 * no null bucket at all (`bucketSpend == null`), in which case a sentence about
 * money that does not exist would be worse than silence. A bucketSpend of 0 is a
 * real answer and keeps the banner.
 *
 * Every clause is DROPPED at 0 rather than rendered with a zero: "0 contents
 * match more than one Country rule" is a sentence about nothing.
 *
 * The counts come from `contentsByAssignmentStage` (the portfolio's parent
 * contents) while the money comes from the optimization's content set, which
 * excludes skipped and newly-added contents. They can disagree slightly. That is
 * accepted — the sentence is directional — and deriving the counts from the
 * optimization payload instead is not an option: it cannot supply them at all.
 */
export function buildContentLabelBucketWarning({
  needsDecisionCount,
  unlabeledCount,
  categoryName,
  bucketSpend,
}: WarningArgs): ContentLabelBucketWarning | null {
  const total = needsDecisionCount + unlabeledCount;
  if (total <= 0) return null;
  if (bucketSpend == null) return null;

  const needsDecisionClause =
    needsDecisionCount > 0
      ? needsDecisionCount === 1
        ? `1 content matches more than one ${categoryName} rule`
        : `${needsDecisionCount} contents match more than one ${categoryName} rule`
      : '';

  // The short form ("1 is unlabeled") only works when the needs-decision clause
  // has already supplied the subject; alone it has to say "content(s)" itself.
  const unlabeledClause =
    unlabeledCount > 0
      ? needsDecisionClause
        ? `${unlabeledCount} ${unlabeledCount === 1 ? 'is' : 'are'} unlabeled`
        : `${unlabeledCount} ${unlabeledCount === 1 ? 'content is' : 'contents are'} unlabeled`
      : '';

  const subject = [needsDecisionClause, unlabeledClause].filter(Boolean).join(', while ');
  const pronoun = total === 1 ? 'it' : 'them';

  return {
    lead: `${subject}, so `,
    trail: ' of average daily spend sits in its own bucket instead of being counted under a label.',
    bucketSpend,
    resolveLead: `Resolve ${pronoun} in `,
    resolveTrail: ` to include ${pronoun} in the proposal.`,
  };
}
